/**
 * The confirmation panel shown in place of the form once a submission has been
 * accepted by the server.
 *
 * It replaces a toast: a toast fires as the dialog is torn down, so the
 * acknowledgement and the thing being acknowledged are never on screen
 * together, and there is no moment where the user is told the work is done and
 * can say "ok". Here the dialog stays put and closing is an explicit act.
 *
 * Purely presentational — it neither knows nor closes anything. Each wrapper
 * decides what "done" means: dismiss for the footer dialog, lift the gate for
 * onboarding.
 */

type Props = {
  title?: string;
  message?: string;
  doneLabel?: string;
  onDone: () => void;
};

const CheckIcon = (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const FeedbackThankYou: React.FC<Props> = ({
  title = "Thanks for the feedback!",
  message = "We read every response, and yours directly shapes what we fix, improve, and build next. It genuinely helps.",
  doneLabel = "Done",
  onDone,
}) => (
  // No `flex-1` here, unlike FeedbackFields: there is nothing elastic to fill
  // the panel with, so the dialog shrinks to this content's natural height.
  <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-6 pb-8 pt-10 text-center">
    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-royal-blue-100 text-royal-blue-500 [.dark_&]:bg-royal-blue-950 [.dark_&]:text-royal-blue-300">
      {CheckIcon}
    </div>

    <h2 className="text-xl font-bold text-gray-900 [.dark_&]:text-zinc-100 mb-2">
      {title}
    </h2>

    <p className="text-sm text-gray-600 [.dark_&]:text-zinc-400 mb-7 max-w-md">
      {message}
    </p>

    <button
      type="button"
      onClick={onDone}
      autoFocus
      className="bg-royal-blue-500 text-white py-3 text-sm px-8 rounded-md font-medium hover:bg-[#5854c7]"
    >
      {doneLabel}
    </button>
  </div>
);

export default FeedbackThankYou;
